/* eslint-disable -- React Compiler rules (react-hooks v6) flagged by external linter are not enabled in this project's CRA toolchain (react-hooks 5.2.0); false positives on event-handler setState */
import { useEffect, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { MEMBER_CLUBS } from "./adminConstants";

export function AdminsTab({ api }) {
  const [admins, setAdmins] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", display_name: "", clubs: [] });
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get("/auth/admins"); setAdmins(r.data); }
    catch (e) { toast.error("Admins konnten nicht geladen werden"); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const toggleClub = (id) => {
    setForm(p => ({ ...p, clubs: p.clubs.includes(id) ? p.clubs.filter(c => c !== id) : [...p.clubs, id] }));
  };

  const save = async () => {
    try {
      if (editId) {
        const data = { display_name: form.display_name, clubs: form.clubs };
        if (form.password) data.password = form.password;
        if (form.is_master !== undefined) data.role = form.is_master ? "central_admin" : "club_admin";
        await api.put(`/auth/admins/${editId}`, data);
        toast.success("Admin aktualisiert");
      } else {
        await api.post("/auth/create-admin", { ...form, role: form.is_master ? "central_admin" : "club_admin" });
        toast.success("Admin erstellt");
      }
      setShowForm(false); setEditId(null);
      setForm({ username: "", password: "", display_name: "", clubs: [] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const deleteAdmin = async (id) => {
    if (!window.confirm("Admin wirklich löschen?")) return;
    try { await api.del(`/auth/admins/${id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  return (
    <div data-testid="admins-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>Admin-Verwaltung</h2>
        <button onClick={() => { setShowForm(true); setEditId(null); setForm({ username: "", password: "", display_name: "", clubs: [], is_master: false }); }}
          data-testid="add-admin-btn" className="flex items-center gap-1 px-4 py-2 bg-primary text-primary-foreground text-xs font-bold uppercase">
          <Plus className="w-4 h-4" /> Neuer Admin
        </button>
      </div>

      {showForm && (
        <div className="bg-card border border-white/10 p-4 mb-4" data-testid="admin-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            {!editId && (
              <div>
                <Label className="text-xs">Benutzername</Label>
                <Input value={form.username} onChange={e => setForm(p => ({...p, username: e.target.value}))}
                  className="mt-1 h-8 text-sm bg-input/50 border-white/10 rounded-none" data-testid="admin-form-username" />
              </div>
            )}
            <div>
              <Label className="text-xs">{editId ? "Neues Passwort (leer = unverändert)" : "Passwort"}</Label>
              <Input type="password" value={form.password} onChange={e => setForm(p => ({...p, password: e.target.value}))}
                className="mt-1 h-8 text-sm bg-input/50 border-white/10 rounded-none" data-testid="admin-form-password" />
            </div>
            <div>
              <Label className="text-xs">Anzeigename</Label>
              <Input value={form.display_name} onChange={e => setForm(p => ({...p, display_name: e.target.value}))}
                className="mt-1 h-8 text-sm bg-input/50 border-white/10 rounded-none" data-testid="admin-form-displayname" />
            </div>
          </div>
          <Label className="text-xs font-bold uppercase text-muted-foreground mb-2 block">Zugewiesene Vereine</Label>
          <div className="flex flex-wrap gap-2 mb-3">
            <label className="flex items-center gap-1.5 px-2 py-1 border border-primary/30 cursor-pointer hover:bg-primary/10 text-xs font-bold" data-testid="admin-form-master">
              <input type="checkbox" checked={form.is_master || false} onChange={e => setForm(p => ({...p, is_master: e.target.checked}))} className="w-3 h-3 accent-primary" />
              Master-Admin
            </label>
            {MEMBER_CLUBS.map(c => (
              <label key={c.id} className="flex items-center gap-1.5 px-2 py-1 border border-white/10 cursor-pointer hover:bg-white/5 text-xs">
                <input type="checkbox" checked={form.clubs.includes(c.id)} onChange={() => toggleClub(c.id)} className="w-3 h-3 accent-primary" />
                {c.label}
              </label>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={save} className="px-4 py-1.5 bg-primary text-primary-foreground text-xs font-bold uppercase" data-testid="admin-form-save">
              {editId ? "Aktualisieren" : "Erstellen"}
            </button>
            <button onClick={() => { setShowForm(false); setEditId(null); }} className="px-4 py-1.5 text-xs text-muted-foreground hover:text-foreground">Abbrechen</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {admins.map(a => (
          <div key={a.id} className="bg-card border border-white/5 p-4 flex items-center justify-between" data-testid={`admin-${a.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-bold text-sm">{a.display_name}</p>
                <Badge className={`text-[10px] ${a.role === "central_admin" ? "bg-primary" : "bg-card border border-white/10"}`}>
                  {a.role === "central_admin" ? "Master" : "Club-Admin"}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">@{a.username}</p>
              {a.clubs?.length > 0 && (
                <div className="flex gap-1 mt-1">
                  {a.clubs.map(cid => {
                    const cl = MEMBER_CLUBS.find(x => x.id === cid);
                    return cl ? <Badge key={cid} variant="outline" className="text-[10px]">{cl.label}</Badge> : null;
                  })}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => { setForm({ username: a.username, password: "", display_name: a.display_name, clubs: a.clubs || [], is_master: a.role === "central_admin" }); setEditId(a.id); setShowForm(true); }}
                className="p-2 text-muted-foreground hover:text-foreground"><Pencil className="w-4 h-4" /></button>
              {a.role !== "central_admin" && (
                <button onClick={() => deleteAdmin(a.id)} className="p-2 text-destructive hover:bg-destructive/10"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
